import { defineStore } from 'pinia';
import type { ProjectConfig, BrandingPalette } from '@/types/projectConfig';

/**
 * Maps config palette keys (camelCase) to Vuetify theme color keys (kebab-case).
 */
function toVuetifyColors(palette: BrandingPalette): Record<string, string> {
  const map: Record<string, string> = {};
  const keyMap: Record<string, string> = {
    primary: 'primary',
    secondary: 'secondary',
    success: 'success',
    warning: 'warning',
    error: 'error',
    background: 'background',
    surface: 'surface',
    onPrimary: 'on-primary',
    onSecondary: 'on-secondary',
    onSuccess: 'on-success',
    onWarning: 'on-warning',
    onError: 'on-error',
    'on-surface': 'on-surface',
    'focus-ring': 'focus-ring',
  };
  for (const [key, value] of Object.entries(palette)) {
    if (value != null) {
      const vuetifyKey = keyMap[key] ?? key;
      map[vuetifyKey] = value;
    }
  }
  return map;
}

interface ProjectConfigState {
  config: ProjectConfig | null;
  loading: boolean;
  error: string | null;
}

export const useProjectConfigStore = defineStore('projectConfig', {
  state: (): ProjectConfigState => ({
    config: null,
    loading: false,
    error: null,
  }),

  getters: {
    logoUrl: (state): string | null => {
      const logo = state.config?.branding?.logo;
      return logo ?? null;
    },
    palette: (state): Record<string, string> => {
      const p = state.config?.branding?.palette;
      return p ? toVuetifyColors(p) : {};
    },
  },

  actions: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const url = `/project.json?t=${Date.now()}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to load project config: ${response.status}`);
        }
        const data = (await response.json()) as ProjectConfig;
        this.config = data;
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Unknown error';
        this.error = message;
        console.warn('[projectConfig] Could not load project.json, using defaults:', message);
        this.config = null;
      } finally {
        this.loading = false;
      }
    },
  },
});
